import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ModalRoot,
  ModalBackdrop,
  ModalPositioner,
  ModalContent,
  ModalTitle,
  FallbackImage,
} from '@rcw/ui';
import { useTranslation } from 'i18n/LocaleContext';
import { useClaimRewardMutation } from 'app/api/gatewayApiSlice';

function CloseIcon() {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'>
      <path stroke='#39a9f7' strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10M15 9l-6 6M9 9l6 6' />
    </svg>
  );
}

export function RewardSelectionDialog({
  isOpen,
  onOpenChange,
  allProducts,
  productImages,
  userRuleStatus,
  idToken,
  username,
  onClaimed,
}) {
  const { t } = useTranslation();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [error, setError] = useState(null);

  const [claimReward, { isLoading: isClaiming }] = useClaimRewardMutation();

  const handleClose = () => {
    onOpenChange({ open: false });
    setTimeout(() => {
      setSelectedProduct(null);
      setError(null);
    }, 200);
  };

  // A rule is claimable only when we can name both the product and the rule it
  // belongs to. `userRuleStatus` arrives pre-normalised: an id is present only
  // if the backend supplied it, and loyalty rules commonly have just the
  // userRuleId. Require at least one — a claim naming neither cannot be
  // attributed to the right reward.
  const hasRuleIdentifier = Boolean(
    userRuleStatus?.userRuleId || userRuleStatus?.userSubscriptionRuleId,
  );
  const canConfirm = Boolean(selectedProduct?.id) && hasRuleIdentifier && Boolean(username);

  const handleConfirm = async () => {
    if (isClaiming) return;
    if (!selectedProduct?.id || !username || !hasRuleIdentifier) {
      setError(t(
        'RVS.COMPONENT.LOYALTY_PROGRAM.ERROR_CLAIMING',
        'This reward cannot be claimed right now. Please refresh the page and try again.',
      ));
      return;
    }
    setError(null);
    try {
      const res = await claimReward({
        username,
        productId: selectedProduct.id,
        source: userRuleStatus?.source,
        userRuleId: userRuleStatus?.userRuleId,
        userSubscriptionRuleId: userRuleStatus?.userSubscriptionRuleId,
        idToken,
      }).unwrap();

      if (res?.isSuccess === false) {
        setError(res.message || t('RVS.COMPONENT.LOYALTY_PROGRAM.ERROR_CLAIMING', 'Failed to claim instrument.'));
        return;
      }

      if (onClaimed) onClaimed();
      handleClose();
    } catch (err) {
      console.error('[RewardSelectionDialog] Error:', err);
      setError(err?.data?.message || err?.message || t('RVS.COMPONENT.LOYALTY_PROGRAM.ERROR_CLAIMING', 'An unexpected error occurred.'));
    }
  };

  const products = Array.isArray(allProducts) ? allProducts : [];

  const getProductImage = (title) => {
    const img = Array.isArray(productImages) ? productImages.find((i) => i.title === title) : null;
    return img?.imageUrl || null;
  };

  return (
    <ModalRoot
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <ModalBackdrop className='!bg-black/80' />
      <ModalPositioner className='!p-0'>
        <ModalContent className='!max-w-4xl !flex !flex-col !overflow-hidden !rounded-3xl !shadow-2xl md:p-8 relative mx-2 bg-white text-black text-sm'>
          <div className='relative'>
            <button
              type='button'
              onClick={handleClose}
              className='absolute top-5 right-4 md:top-0 md:right-0 p-1 md:p-0 hover:bg-black/5 rounded-full transition-colors'
              aria-label={t('RVS.COMPONENT.LOYALTY_PROGRAM.BUTTON.CANCEL', 'Close Dialog')}
            >
              <CloseIcon />
            </button>
            <div className='px-6 pt-6'>
              <ModalTitle className='!text-2xl !text-black'>
                {t('RVS.COMPONENT.LOYALTY_PROGRAM.REWARDS_CHOOSE_INSTRUMENT', 'Choose Your Instrument')}
              </ModalTitle>
            </div>
          </div>

          <div className='flex-1 min-h-0 px-6 py-6 overflow-y-auto'>
            {error && (
              <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm mb-6'>
                {error}
              </div>
            )}

            <div className='space-y-6'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                {products.map((product) => (
                  <button
                    key={product.id}
                    type='button'
                    onClick={() => setSelectedProduct(product)}
                    className={`p-4 rounded-xl border text-left transition-all flex items-center gap-4 ${
                      selectedProduct?.id === product.id
                        ? 'border-roland-blue bg-roland-blue/5 ring-1 ring-roland-blue'
                        : 'border-black/10 hover:border-roland-blue/50'
                    }`}
                    aria-label={`${t('RVS.COMPONENT.LOYALTY_PROGRAM.BUTTON.PICK', 'Pick')} ${product.title}`}
                  >
                    <FallbackImage
                      src={getProductImage(product.title)}
                      alt={product.title}
                      imgClassName='w-[60px] h-[60px] object-contain rounded-md'
                    />
                    <div>
                      <p className='font-bold text-sm'>{product.title}</p>
                    </div>
                  </button>
                ))}
              </div>

              <div className='flex flex-col md:flex-row justify-end gap-4 py-4 sticky bottom-0 bg-white shadow-lg'>
                <button
                  type='button'
                  onClick={handleClose}
                  className='btn-secondary order-2 md:order-1 px-14'
                  aria-label={t('RVS.COMPONENT.LOYALTY_PROGRAM.BUTTON.CANCEL', 'Cancel')}
                  disabled={isClaiming}
                >
                  {t('RVS.COMPONENT.LOYALTY_PROGRAM.BUTTON.CANCEL', 'Cancel')}
                </button>
                <button
                  type='button'
                  disabled={!canConfirm || isClaiming}
                  onClick={handleConfirm}
                  className={`btn-primary order-1 md:order-2 px-14 min-w-[140px] flex items-center justify-center ${!canConfirm || isClaiming ? 'opacity-50 cursor-not-allowed' : ''}`}
                  aria-label={selectedProduct
                    ? `${t('RVS.COMPONENT.LOYALTY_PROGRAM.BUTTON.PICK', 'Pick')} ${selectedProduct.title}`
                    : t('RVS.COMPONENT.LOYALTY_PROGRAM.BUTTON.PICK', 'Pick an Instrument')}
                >
                  {isClaiming ? (
                    <span className='w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin' />
                  ) : (
                    selectedProduct
                      ? `${t('RVS.COMPONENT.LOYALTY_PROGRAM.BUTTON.PICK', 'Pick')} ${selectedProduct.title}`
                      : t('RVS.COMPONENT.LOYALTY_PROGRAM.BUTTON.PICK', 'Pick an Instrument')
                  )}
                </button>
              </div>
            </div>
          </div>
        </ModalContent>
      </ModalPositioner>
    </ModalRoot>
  );
}

RewardSelectionDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  allProducts: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string,
    }),
  ).isRequired,
  productImages: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      imageUrl: PropTypes.string,
    }),
  ),
  userRuleStatus: PropTypes.shape({
    source: PropTypes.string,
    userRuleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    userSubscriptionRuleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  idToken: PropTypes.string.isRequired,
  username: PropTypes.string.isRequired,
  onClaimed: PropTypes.func,
};
